import React, { useState, useEffect } from 'react';
import { Container, Tab } from 'react-bootstrap';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../api';

import Loader from '../../components/common/Loader';
import AppCapabilities from '../../components/admin/AppCapabilities';
import ThirdPartyApiKeys from '../../components/applications/ThirdPartyApiKeys';
import ApplicationForm from '../../components/admin/ApplicationForm';
import VAccountsForm from '../../components/admin/VAccountsForm';
import StatementInfoForm from '../../components/applications/StatementInfoForm';
import OperationalWallets from './OperationalWallets';
import ThirdPartyWebhooks from '../../components/applications/ThirdPartyWebhooks';
import FreezeModal from '../../components/common/FreezeModal';
import { formatFreezeReasons, hasSuperuserAccess } from '../../utils';
import { SUPERUSER_LEVEL } from '../../constants';
import {
  AppProfileNavItem,
  AppProfileTabColumn,
  AppProfileDetailColumn,
  AdminAppDetailView,
  AppCardHeader,
  AppCardBody,
  AppDetailHeader,
} from '../../components/applications/AppProfileViewComponents';
import AdminKYCFlow from './AdminKYCFlow';

function Application({ t, env, isMobile, setAlert, user }) {
  /**
   * Renders the detailed view of an application for superusers. Example url  where this
   * component can be viewed is localadmin.silamoney.com:8000/admin/app/6/detail
   */
  const location = useLocation();
  const navigate = useNavigate();
  const { action, id } = useParams();
  const [header, setHeader] = useState(action);
  const [isLoading, setIsLoading] = useState(true);
  const [app, setApp] = useState({});
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState(app.enabled_features || []);

  const redirect = () => {
    navigate({ pathname: '/admin/apps', state: { from: location.pathname } });
  };

  const getApplication = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.getApplication(id);
      if (response.success) {
        setApp(response.app);
        setIsLoading(false);
      } else {
        redirect();
      }
    } catch (error) {
      redirect();
      setAlert({ message: error, variant: 'warning' });
    }
  };

  useEffect(() => {
    setHeader(action);
  }, [action]);

  useEffect(() => {
    getApplication();
  }, [env]);

  useEffect(() => {
    setEnabledFeatures(app.enabled_features || []);
  }, [app]);

  const appFreezeUnfreeze = async ({ freeze_reasons }) => {
    const reasonsWithStatus = formatFreezeReasons(
      Object.keys(app.freeze_reasons),
      new Set(freeze_reasons)
    );
    const response = await adminApi.appFreezeUnfreeze(app.id, reasonsWithStatus);

    if (response.status_code === 200) {
      setAlert({
        message: response.message,
        variant: 'success',
      });
    } else if (!response.success) {
      if (response.status_code === 400) {
        setAlert({
          message: response.message,
          variant: 'danger',
        });
      } else if (response.status_code === 409) {
        setAlert({
          message: response.message,
          variant: 'warning',
        });
      }
    }

    const responseFreezeReasons = response.freeze_reasons ? response.freeze_reasons : {};
    setApp({
      ...app,
      frozen: Object.values(responseFreezeReasons).length !== 0,
      freeze_reasons: responseFreezeReasons,
    });

    setShowFreezeModal(false);
  };

  // variable used in FreezeModal
  const name = app.nickname;
  const type = t('common:labels.app');

  return (
    <Container className="main-content-container p-3 p-md-5 d-flex flex-column flex-grow-1">
      <AppDetailHeader t={t} />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <AppCardHeader setShowFreezeModal={setShowFreezeModal} app={app} t={t} user={user} />

          <AppCardBody header={header} setHeader={setHeader}>
            <AppProfileTabColumn>
              <AppProfileNavItem type={header} appId={id} t={t} route="detail" />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="virtual-accounts"
              />
              <AppProfileNavItem type={header} appId={id} t={t} route="statement-info" />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="operational-wallets"
              />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="capabilities"
              />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="access-keys"
              />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="provider-webhooks"
              />
              <AppProfileNavItem
                type={header}
                appId={id}
                t={t}
                disabled={app.deleted}
                route="kyc-flows"
              />
            </AppProfileTabColumn>
            <AppProfileDetailColumn>
              <Tab.Content className="w-100">
                <Tab.Pane eventKey="detail" className="px-4">
                  {app.team_members.filter((member) => member.role_name === 'owner').length !==
                    0 && <AdminAppDetailView app={app} t={t} />}
                  <ApplicationForm
                    initialValues={app}
                    onSave={getApplication}
                    isMobile={isMobile}
                    enabledFeatures={enabledFeatures}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="virtual-accounts" className="px-4">
                  <VAccountsForm initialValues={app} onSave={getApplication} />
                </Tab.Pane>
                <Tab.Pane eventKey="statement-info">
                  <StatementInfoForm
                    admin
                    isMobile={isMobile}
                    initialValues={app}
                    user={user}
                    onSave={getApplication}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="operational-wallets">
                  <OperationalWallets app={app} getApplication={getApplication} />
                </Tab.Pane>
                <Tab.Pane eventKey="capabilities">
                  <AppCapabilities
                    enabledFeatures={enabledFeatures}
                    setEnabledFeatures={setEnabledFeatures}
                    id={id}
                    handle={app.handle}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="access-keys">
                  <ThirdPartyApiKeys admin appId={app.id} onSave={getApplication} />
                </Tab.Pane>
                <Tab.Pane eventKey="provider-webhooks">
                  <ThirdPartyWebhooks admin appId={app.id} />
                </Tab.Pane>
                <Tab.Pane eventKey="kyc-flows" className="px-4">
                  <AdminKYCFlow appHandle={app.handle} frozen={app.frozen} t={t} />
                </Tab.Pane>
              </Tab.Content>
            </AppProfileDetailColumn>
          </AppCardBody>
        </>
      )}
      {showFreezeModal && (
        <FreezeModal
          show={showFreezeModal}
          data={{}}
          handleFreeze={appFreezeUnfreeze}
          reasons={app.freeze_reasons}
          setShow={setShowFreezeModal}
          isAdmin={hasSuperuserAccess(user, [SUPERUSER_LEVEL.FULL_ACCESS])}
          name={name}
          type={type}
        />
      )}
    </Container>
  );
}

export default Application;
